<template>
  <v-dialog v-model="dialog" max-width="90%">
    <v-card>
      <v-card-title class="grey--text text-subtitle-1">
        Grafico Storico Temperatura - {{ therm.nome }}
        <v-spacer></v-spacer>
        <v-btn text color="grey" @click="dialog = false">Chiudi</v-btn>
      </v-card-title>
      <v-card-text>
        <template v-if="loading">
          <v-row justify="center" align="center">
            <v-progress-circular
              indeterminate
              width="1"
              color="green"
            ></v-progress-circular>
          </v-row>
        </template>
        <template v-else>
          <v-row class="px-8 mt-4">
            <v-col cols="3">
              <v-select
                outlined
                :items="intervals"
                label="Intervallo Iniziale"
                @change="fetchRecords"
                v-model="start"
                item-value="value"
                item-text="text"
              />
            </v-col>
            <v-col cols="2">
              <v-select
                outlined
                :items="[15, 30, 60, 120]"
                label="risoluzione (minuti)"
                @change="fetchRecords"
                v-model="risoluzione"
              />
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="sid_confronto"
                label="Confronta con"
                outlined
                :items="therms_items"
              />
            </v-col>
          </v-row>
          <v-row no-gutters justify="center" class="mt-4">
            <v-col
              cols="12"
              class="text-center grey--text text--lighten-1"
              v-if="records.length === 0"
              >Nessun dato storico disponibile per questo termostato</v-col
            >

            <v-col cols="11">
              <VueApexCharts
                height="580"
                :options="chartOptions"
                :series="chartSeries"
              />
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
//import moment from "moment";
import axios from "axios";
import moment from "moment";
import VueApexCharts from "vue-apexcharts";

export default {
  components: {
    VueApexCharts,
  },
  props: {
    therm: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      start: null,
      end: null,
      loading: false,
      records: [],
      sid_confronto: null,
      records_confronto: [],
      intervals: [
        {
          text: "Ultime 24 ore",
          value: moment().subtract(1, "days").format("X"),
        },
        {
          text: "Ultime 48 ore",
          value: moment().subtract(2, "days").format("X"),
        },
        {
          text: "Ultimi 3 giorni",
          value: moment().subtract(3, "days").format("X"),
        },
        {
          text: "Ultimi 7 giorni",
          value: moment().subtract(7, "days").format("X"),
        },
      ],
      risoluzione: 60, // minuti
    };
  },
  watch: {
    sid_confronto() {
      this.fetchRecordsConfronto();
    },
  },
  computed: {
    therms_items() {
      let internal = this.$store.getters.get_therm
        .filter((t) => t.sid !== this.therm.sid)
        .map((t) => ({
          text: t.nome + (t.isConnected ? "" : " (disconnesso)"),
          value: t.sid,
        }));
      let external = this.$store.getters.get_ext_therm.map((t) => ({
        text: t.nome + " [Esterno]" + (t.isConnected ? "" : " (disconnesso)"),
        value: t.sid,
      }));
      return [{ text: "NESSUN TERMOSTATO", value: null }, ...internal, ...external];
    },
    chartOptions() {
      return {
        chart: {
          id: "basic-bar",
          animations: {
            enabled: false,
            easing: "linear",
            speed: 0, // oppure usare easing diverso
          },
        },
        annotations: {
          points: [
            {
              x: this.max_point ? this.max_point.ts * 1000 : null,
              y: this.max_point ? this.max_point.value : null,
              marker: {
                size: 6,
                fillColor: "red",
                strokeColor: "red",
                radius: 2,
                cssClass: "apexcharts-custom-class",
              },
              label: {
                borderColor: "transparent",
                offsetY: 0,
                style: {
                  color: "red",
                  background: "transparent",
                },
                text: this.max_point ? `Max: ${this.max_point.value} °C` : "",
              },
            },
            {
              x: this.min_point ? this.min_point.ts * 1000 : null,
              y: this.min_point ? this.min_point.value : null,
              marker: {
                size: 6,
                fillColor: "blue",
                strokeColor: "blue",
                radius: 2,
                cssClass: "apexcharts-custom-class",
              },
              label: {
                borderColor: "transparent",
                offsetY: 0,
                style: {
                  color: "blue",
                  background: "transparent",
                },
                text: this.min_point ? `Min: ${this.min_point.value} °C` : "",
              },
            },
          ],
          yaxis: [
            {
              y: 0,
              yAxisIndex: 0, // primo asse Y
              borderColor: "#9E9E9E",
              strokeDashArray: 4,
              label: {
                borderColor: "transparent",
                style: { color: "#9E9E9E", background: "transparent" },
                text: "0 °C",
              },
            },
          ],
        },
        xaxis: {
          type: "datetime",
          labels: {
            datetimeUTC: false,
          },
        },
        yaxis: {
          min: this.min_point < -5 ? Math.floor(this.min_point.value) - 5 : -5,
          max: this.max_point > 30 ? Math.ceil(this.max_point.value) + 5 : 30,
        },
        stroke: {
          curve: "smooth",
          width: 2,
          dashArray: [0, 8, 5, 0],
          colors: ["#FF4560", "#9C27B0", "transparent"],
        },
        dataLabels: {
          enabled: false,
        },
        markers: {
          size: 0,
        },
        tooltip: {
          x: {
            format: "dd MMM HH:mm",
          },
        },
      };
    },
    chartSeries() {
      const series = [
        {
          name: "Temperatura",
          type: "line",
          color: "#FF4560",
          data: this.records.map((r) => [
            r.ts * 1000,
            r.value ? parseFloat(r.value.toFixed(1)) : null,
          ]),
        },
        {
          name: "Setpoint",
          type: "line",
          color: "#9C27B0",
          data: this.records.map((r) => [
            r.ts * 1000,
            r.set_point ? parseFloat(r.set_point.toFixed(1)) : null,
          ]),
        },
        {
          name: "Consumi (litri/10min)",
          type: "column",
          color: "#B2DFDB",
          data: this.records.map((r) => [
            r.ts * 1000,
            r.burning_time !== undefined
              ? parseFloat((r.burning_time / 360).toFixed(1))
              : null,
          ]),
        },
      ];
      if (this.sid_confronto && this.records_confronto.length > 0)
        series.push({
          name: "Confronto",
          type: "line",
          color: "#00E676",
          data: this.records_confronto.map((r) => [
            r.ts * 1000,
            r.value ? parseFloat(r.value.toFixed(1)) : null,
          ]),
        });
      return series;
    },
    max_point() {
      if (!Array.isArray(this.records) || this.records.length === 0)
        return null;
      const valid = this.records.filter(
        (r) => r && typeof r.value === "number" && !Number.isNaN(r.value)
      );
      if (valid.length === 0) return null;
      const maxRec = valid.reduce(
        (max, r) => (r.value > max.value ? r : max),
        valid[0]
      );
      return {
        value: Number(maxRec.value.toFixed(1)),
        ts: maxRec.ts, // epoch (secondi)
      };
    },
    min_point() {
      if (!Array.isArray(this.records) || this.records.length === 0)
        return null;
      const valid = this.records.filter(
        (r) => r && typeof r.value === "number" && !Number.isNaN(r.value)
      );
      if (valid.length === 0) return null;
      const minRec = valid.reduce(
        (min, r) => (r.value < min.value ? r : min),
        valid[0]
      );
      return {
        value: Number(minRec.value.toFixed(1)),
        ts: minRec.ts, // epoch (secondi)
      };
    },
  },
  methods: {
    open() {
      this.dialog = true;
      this.start = moment().subtract(1, "days").format("X");
      this.end = moment().format("X");
      this.fetchRecords();
    },
    fetchRecords() {
      if (!this.therm || !this.therm.sid) return;
      let auth = this.$store.getters.get_credential;
      let url = process.env.VUE_APP_API_ROOT;
      //this.loading = true;
      axios
        .get(
          `${url}/thermostat/${this.therm.sid}/records/start/${this.start}/stop/${this.end}/resolution/${this.risoluzione}`,
          { auth: auth }
        )
        .then((response) => {
          this.records = response.data;
        })
        .catch((error) => {
          console.error("Error fetching therm records:", error);
        })
        .finally(() => {
          this.loading = false;
        });

      if (this.sid_confronto) {
        this.fetchRecordsConfronto();
      } else {
        this.records_confronto = [];
      }
    },
    fetchRecordsConfronto() {
      if (!this.therm || !this.sid_confronto) return;
      let auth = this.$store.getters.get_credential;
      let url = process.env.VUE_APP_API_ROOT;
      //this.loading = true;
      axios
        .get(
          `${url}/thermostat/${this.sid_confronto}/records/start/${this.start}/stop/${this.end}/resolution/${this.risoluzione}`,
          { auth: auth }
        )
        .then((response) => {
          this.records_confronto = response.data;
        })
        .catch((error) => {
          console.error("Error fetching confronto therm records:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>